import { useEffect, useState } from 'react';
import {
    Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography, Button, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField,
    Alert, MenuItem, IconButton, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../services/api';
import RegisterAccess from '../../utils/registerAccess';
import HeaderSession from '../../utils/headerSession';
import TabelaLoading from '../../load/tabelaLoading';
import ListaVazia from '../../utils/listaVazia';

export default function Municipios() {
    const dispatch = useDispatch();
    const [municipios, setMunicipios] = useState(useSelector(state => state?.municipios?.data) ?? []);
    const [provincias, setProvincias] = useState([]);
    const [filtroProvinciaId, setFiltroProvinciaId] = useState('');
    const [loading, setLoading] = useState(false);

    // Dialog criar
    const [dialogOpen, setDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [novoMunicipio, setNovoMunicipio] = useState({ designacao: '', provinciaId: '' });
    const [municipiosMunicDialog, setMunicipiosMunicDialog] = useState([]);

    // Dialog confirmar eliminação
    const [deleteDialog, setDeleteDialog] = useState({ open: false, municipio: null });
    const [deleting, setDeleting] = useState(false);
    const [deleteFeedback, setDeleteFeedback] = useState(null);

    useEffect(() => {
        api.get('/provincias')
            .then(res => setProvincias(res.data.provincias ?? []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        const endpoint = filtroProvinciaId
            ? `/municipios-by-provincia/${filtroProvinciaId}`
            : '/municipios';

        api.get(endpoint)
            .then(res => {
                const lista = filtroProvinciaId
                    ? (res.data.municipios ?? [])
                    : (res.data.municipios ?? []);
                setMunicipios(lista);
                dispatch({ type: 'municipios', payload: { municipios: lista } });
            })
            .finally(() => setLoading(false));
    }, [filtroProvinciaId]);

    // ---- Criar município ----
    function handleOpenDialog() {
        setNovoMunicipio({ designacao: '', provinciaId: '' });
        setFeedback(null);
        setMunicipiosMunicDialog([]);
        setDialogOpen(true);
    }

    function handleClose() {
        if (submitting) return;
        setDialogOpen(false);
    }

    function handleFieldChange(e) {
        const { name, value } = e.target;
        setNovoMunicipio(prev => ({ ...prev, [name]: value }));
    }

    function handleSubmit(e) {
        e.preventDefault();
        if (!novoMunicipio.designacao.trim() || !novoMunicipio.provinciaId) return;
        setSubmitting(true);
        setFeedback(null);

        api.post('/municipios/create', {
            designacao: novoMunicipio.designacao.trim(),
            provinciaId: novoMunicipio.provinciaId
        })
            .then(res => {
                const novo = res.data.municipio ?? res.data;
                const lista = [...municipios, novo];
                setMunicipios(lista);
                dispatch({ type: 'municipios', payload: { municipios: lista } });
                setFeedback({ type: 'success', message: 'Município registado com sucesso.' });
                setNovoMunicipio({ designacao: '', provinciaId: '' });
                setTimeout(() => setDialogOpen(false), 1200);
            })
            .catch(err => {
                const msg = err.response?.data?.message ?? 'Erro ao registar o município.';
                setFeedback({ type: 'error', message: msg });
            })
            .finally(() => setSubmitting(false));
    }

    // ---- Eliminar município ----
    function handleOpenDelete(municipio) {
        setDeleteFeedback(null);
        setDeleteDialog({ open: true, municipio });
    }

    function handleCloseDelete() {
        if (deleting) return;
        setDeleteDialog({ open: false, municipio: null });
    }

    function handleConfirmDelete() {
        const { municipio } = deleteDialog;
        if (!municipio) return;
        setDeleting(true);
        setDeleteFeedback(null);

        api.delete(`/municipio/delete/${municipio._id ?? municipio.id}`)
            .then(() => {
                const lista = municipios.filter(m => (m._id ?? m.id) !== (municipio._id ?? municipio.id));
                setMunicipios(lista);
                dispatch({ type: 'municipios', payload: { municipios: lista } });
                setDeleteDialog({ open: false, municipio: null });
            })
            .catch(err => {
                const msg = err.response?.data?.message ?? 'Erro ao eliminar o município.';
                setDeleteFeedback({ type: 'error', message: msg });
            })
            .finally(() => setDeleting(false));
    }

    const provinciaLabel = (provinciaId) => {
        const p = provincias.find(p => (p._id ?? p.id) === provinciaId);
        return p?.designacao ?? provinciaId ?? '—';
    };

    return (
        <>
            <RegisterAccess page={'gestão de municípios'} />

            <HeaderSession title='MUNICÍPIOS' />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                <TextField
                    select
                    label="Filtrar por Província"
                    size="small"
                    value={filtroProvinciaId}
                    onChange={e => setFiltroProvinciaId(e.target.value)}
                    sx={{ minWidth: 220 }}
                >
                    <MenuItem value="">Todas as províncias</MenuItem>
                    {provincias.map(p => (
                        <MenuItem key={p._id ?? p.id} value={p._id ?? p.id}>{p.designacao}</MenuItem>
                    ))}
                </TextField>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenDialog}
                    sx={{ backgroundColor: '#85287e', '&:hover': { backgroundColor: '#6a1f63' } }}
                >
                    Adicionar Município
                </Button>
            </div>

            {loading && <TabelaLoading />}

            {!loading && municipios.length === 0 && <ListaVazia text="Nenhum município registado." />}

            {!loading && municipios.length > 0 && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#dcdede' }}>
                            <TableRow>
                                <TableCell><Typography noWrap><strong>#</strong></Typography></TableCell>
                                <TableCell><Typography noWrap><strong>Designação</strong></Typography></TableCell>
                                <TableCell><Typography noWrap><strong>Província</strong></Typography></TableCell>
                                <TableCell align="center"><Typography noWrap><strong>Ações</strong></Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {municipios.map((mun, idx) => (
                                <TableRow key={mun._id ?? mun.id ?? idx}>
                                    <TableCell>{idx + 1}</TableCell>
                                    <TableCell>{mun.designacao}</TableCell>
                                    <TableCell>{provinciaLabel(mun.provinciaId ?? mun.provincia?._id)}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Eliminar">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleOpenDelete(mun)}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialog criar município */}
            <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="sm">
                <form onSubmit={handleSubmit}>
                    <DialogTitle>Novo Município</DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        {feedback && (
                            <Alert severity={feedback.type}>{feedback.message}</Alert>
                        )}
                        <TextField
                            select
                            label="Província"
                            name="provinciaId"
                            fullWidth
                            required
                            size="small"
                            value={novoMunicipio.provinciaId}
                            onChange={handleFieldChange}
                        >
                            {provincias.map(p => (
                                <MenuItem key={p._id ?? p.id} value={p._id ?? p.id}>{p.designacao}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Designação"
                            name="designacao"
                            fullWidth
                            required
                            size="small"
                            value={novoMunicipio.designacao}
                            onChange={handleFieldChange}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} disabled={submitting}>Cancelar</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting || !novoMunicipio.designacao.trim() || !novoMunicipio.provinciaId}
                            sx={{ backgroundColor: '#85287e', '&:hover': { backgroundColor: '#6a1f63' } }}
                        >
                            {submitting ? 'A guardar...' : 'Guardar'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Dialog confirmar eliminação */}
            <Dialog open={deleteDialog.open} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
                <DialogTitle>Confirmar eliminação</DialogTitle>
                <DialogContent>
                    {deleteFeedback && (
                        <Alert severity={deleteFeedback.type} sx={{ mb: 1 }}>{deleteFeedback.message}</Alert>
                    )}
                    <Typography>
                        Tem a certeza que deseja eliminar o município{' '}
                        <strong>{deleteDialog.municipio?.designacao}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDelete} disabled={deleting}>Cancelar</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDelete}
                        disabled={deleting}
                    >
                        {deleting ? 'A eliminar...' : 'Eliminar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
